import React, { useCallback, useRef } from 'react';
import { styled } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { NEW_MODE } from '../../common/constants';
import type { Metrics } from '../../common/types';
import { useGoalCounter } from '../../controllers/goal-counter';
import { useMetricActions } from '../../controllers/metric-actions';
import { NewMetric, MetricWithSummary } from './main';

type MetricListProps = {
	canManageFields: boolean;
	metrics: Metrics;
};

export const MetricList = ({ metrics, canManageFields }: MetricListProps) => {
	const isSingleMetricConfiguredForTheProject = Object.keys(metrics).length === 1;
	const [{ selectedMetricId, metricMode }, { updatePath }] = useMetricActions();
	const isInitialSlaPageLoad = useRef(true); // This is used to show Metrics in View Mode on SLA page load when only one metric is configured
	const [, actions] = useGoalCounter();

	const onCancel = useCallback(() => {
		const calculateTotalNumberOfGoalsInProject = () => {
			let goalCount = 0;
			Object.keys(metrics).forEach((key) => {
				goalCount += Object.keys(metrics[key].config.goals).length;
			});
			actions.setGoalCounter(goalCount - Object.keys(metrics).length);
		};
		updatePath();
		calculateTotalNumberOfGoalsInProject();
		if (metricMode === NEW_MODE) {
			actions.setShouldFocusOnAddSlaButton(true);
		}
	}, [updatePath, metricMode, metrics, actions]);

	const setIsInitialSlaPageLoadToFalse = () => {
		isInitialSlaPageLoad.current = false;
	};

	return (
		<>
			<NewMetric metricMode={metricMode} onCancel={onCancel} canManageFields={canManageFields} />
			<TimeMetricList>
				{/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
				{Object.keys(metrics).reduce<Array<any>>((metricList, metricKey) => {
					const metric = metrics[metricKey];
					metricList.push(
						<TimeMetricListItem>
							<MetricWithSummary
								key={metricKey}
								metric={metric}
								metrics={metrics}
								canManageFields={canManageFields}
								id={metricKey}
								isSelected={selectedMetricId === metricKey}
								metricMode={metricMode}
								onCancel={onCancel}
								isSingleMetricConfiguredForTheProject={isSingleMetricConfiguredForTheProject}
								isInitialSlaPageLoad={isInitialSlaPageLoad.current}
								setIsInitialSlaPageLoadToFalse={setIsInitialSlaPageLoadToFalse}
							/>
						</TimeMetricListItem>,
					);

					return metricList;
				}, [])}
			</TimeMetricList>
		</>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TimeMetricList = styled.ul({
	listStyle: 'none',
	padding: 0,
	margin: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TimeMetricListItem = styled.li({
	borderRadius: '3px',
	boxShadow: token(
		'elevation.shadow.raised',
		'0 1px 1px 0 rgba(9, 30, 66, 0.25), 0 0 1px 0 rgba(9, 30, 66, 0.31)',
	),
	boxSizing: 'border-box',
	border: `1px solid ${token('color.border', 'rgba(9, 30, 66, 0.08)')}`,
	margin: `${token('space.100', '8px')} 0`,
});
